import { useMemo, useState } from 'react'
import { Button, Card, Chip, Select, SelectItem, Switch } from '@heroui/react'

import { loadFixtures, loadReports } from './dataLoaders'
import { FixtureCard } from './FixtureCard'
import { fmtTimestamp } from './formatters'
import { LeaderboardTable } from './LeaderboardTable'

type FilterMode = 'all' | 'failing' | 'image-only'

export function EvalDashboard() {
  const fixtures = useMemo(loadFixtures, [])
  const reports = useMemo(loadReports, [])

  const [selectedReportKey, setSelectedReportKey] = useState<string>(
    reports[0]?.filename ?? ''
  )
  const [filterMode, setFilterMode] = useState<FilterMode>('all')
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set())
  const [showLeaderboard, setShowLeaderboard] = useState(true)

  const selectedReport =
    reports.find((r) => r.filename === selectedReportKey) ?? reports[0] ?? null
  const reportData = selectedReport?.data ?? null

  // Tag inventory for the filter chips, ordered by frequency.
  const sortedTags = useMemo(() => {
    const counts = new Map<string, number>()
    for (const f of fixtures) {
      for (const t of f.meta.tags) counts.set(t, (counts.get(t) ?? 0) + 1)
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  }, [fixtures])

  const visibleFixtures = useMemo(() => {
    return fixtures.filter((f) => {
      if (filterMode === 'image-only' && !f.sourceUrl) return false
      if (filterMode === 'failing') {
        if (!reportData) return true // no run loaded → show everything
        const fixtureResults = reportData.results.filter(
          (r) => r.fixtureId === f.meta.id
        )
        if (fixtureResults.length === 0) return false
        const allClean = fixtureResults.every((r) => r.score?.isClean === true)
        if (allClean) return false
      }
      if (activeTags.size > 0) {
        const tagsOnFixture = new Set(f.meta.tags)
        for (const tag of activeTags) {
          if (!tagsOnFixture.has(tag)) return false
        }
      }
      return true
    })
  }, [fixtures, filterMode, activeTags, reportData])

  const toggleTag = (tag: string) => {
    setActiveTags((prev) => {
      const next = new Set(prev)
      if (next.has(tag)) next.delete(tag)
      else next.add(tag)
      return next
    })
  }

  const reportImageFixtureCount = reportData
    ? reportData.fixtures.filter((f) => f.hasSource).length
    : 0

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
          Phase 1 Eval
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Conversation-parsing fixtures and the model results scored against
          them. Edit a fixture by opening its{' '}
          <code className="font-mono text-xs">meta.json</code> in your editor —
          this page hot-reloads on save. Run{' '}
          <code className="font-mono text-xs">npm run eval:phase1</code> in{' '}
          <code className="font-mono text-xs">/functions</code> to score
          candidate models; restart the dev server to pick up new reports.
        </p>
      </div>

      <Card className="p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[280px] max-w-md">
            {reports.length === 0 ? (
              <div className="text-sm text-slate-500 dark:text-slate-400">
                No eval runs yet — fixture review only.
              </div>
            ) : (
              <Select
                aria-label="Select eval run"
                size="sm"
                label="Run"
                selectedKeys={
                  selectedReport
                    ? new Set([selectedReport.filename])
                    : new Set()
                }
                onSelectionChange={(keys) => {
                  const key = Array.from(keys)[0]
                  if (typeof key === 'string') setSelectedReportKey(key)
                }}
                items={reports}
              >
                {(report) => {
                  const label = `${fmtTimestamp(report.startedAt)} · ${report.data.totalFixtures}f × ${report.data.models.length}m`
                  return (
                    <SelectItem key={report.filename} textValue={label}>
                      {label}
                    </SelectItem>
                  )
                }}
              </Select>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 dark:border-slate-700 pt-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Filter
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant={filterMode === 'all' ? 'flat' : 'light'}
              color={filterMode === 'all' ? 'primary' : 'default'}
              onPress={() => setFilterMode('all')}
            >
              All
            </Button>
            <Button
              size="sm"
              variant={filterMode === 'failing' ? 'flat' : 'light'}
              color={filterMode === 'failing' ? 'warning' : 'default'}
              onPress={() => setFilterMode('failing')}
              isDisabled={!reportData}
            >
              Failing only
            </Button>
            <Button
              size="sm"
              variant={filterMode === 'image-only' ? 'flat' : 'light'}
              color={filterMode === 'image-only' ? 'secondary' : 'default'}
              onPress={() => setFilterMode('image-only')}
            >
              With screenshot
            </Button>
          </div>
          {activeTags.size > 0 && (
            <Button
              size="sm"
              variant="light"
              onPress={() => setActiveTags(new Set())}
            >
              Clear tags ({activeTags.size})
            </Button>
          )}
          <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
            Showing {visibleFixtures.length} of {fixtures.length}
          </span>
        </div>

        {sortedTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {sortedTags.map(([tag, count]) => {
              const active = activeTags.has(tag)
              return (
                <Chip
                  key={tag}
                  size="sm"
                  variant={active ? 'solid' : 'bordered'}
                  color={active ? 'primary' : 'default'}
                  className="cursor-pointer"
                  onClick={() => toggleTag(tag)}
                >
                  {tag} · {count}
                </Chip>
              )
            })}
          </div>
        )}

        {visibleFixtures.length > 0 && (
          <div className="flex flex-wrap gap-1 border-t border-slate-200 dark:border-slate-700 pt-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mr-2 self-center">
              Jump to
            </span>
            {visibleFixtures.map((f) => {
              const fixtureResults =
                reportData?.results.filter((r) => r.fixtureId === f.meta.id) ??
                []
              let mark = ''
              if (fixtureResults.length > 0) {
                const cleanCount = fixtureResults.filter(
                  (r) => r.score?.isClean
                ).length
                mark =
                  cleanCount === fixtureResults.length
                    ? '✓'
                    : cleanCount === 0
                      ? '✗'
                      : '~'
              }
              return (
                <a
                  key={f.slug}
                  href={`#fixture-${f.slug}`}
                  className="font-mono text-[11px] px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
                >
                  {f.slug.slice(0, 2)}
                  {mark && <span className="ml-1">{mark}</span>}
                </a>
              )
            })}
          </div>
        )}
      </Card>

      {reportData && (
        <Card className="p-3 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Model leaderboard ({fmtTimestamp(reportData.startedAt)})
            </h2>
            <Switch
              size="sm"
              isSelected={showLeaderboard}
              onValueChange={setShowLeaderboard}
            >
              Show
            </Switch>
          </div>
          {showLeaderboard && (
            <div className="space-y-3">
              <LeaderboardTable
                data={reportData}
                mode="text"
                title="Text-mode summary (all fixtures)"
              />
              {reportImageFixtureCount > 0 && (
                <LeaderboardTable
                  data={reportData}
                  mode="image"
                  title={`One-shot multimodal summary (${reportImageFixtureCount} screenshot fixtures, vision-capable models only)`}
                />
              )}
            </div>
          )}
        </Card>
      )}

      <div className="space-y-5">
        {visibleFixtures.length === 0 ? (
          <Card className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
            No fixtures match the current filter.
          </Card>
        ) : (
          visibleFixtures.map((fixture) => (
            <FixtureCard
              key={fixture.slug}
              fixture={fixture}
              report={reportData}
            />
          ))
        )}
      </div>
    </div>
  )
}
